/**
 * shadcn chart-pie-donut-text (no legend).
 * @see https://ui.shadcn.com/charts/pie#chart-pie-donut-text
 */

import React, { useMemo } from "react";
import { Label, Pie, PieChart } from "recharts";
import { useTheme } from "next-themes";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import {
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  STATISTICS_NO_DATA_TIMEFRAME_CLASSNAME,
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsChartTooltipRow,
  statsChartTooltipContentClassName,
} from "./statisticsChartTooltip";
import {
  STATS_FONT_FAMILY,
  statsCardHeadlineClassName,
  useStatsMutedTextFill,
} from "./statisticsCardHeadline";
import {
  statisticsShadcnCardChartAreaClassName,
  statisticsShadcnCardClassName,
  statisticsShadcnCardContentClassName,
  statisticsShadcnCardHeaderClassName,
  statisticsShadcnChartContainerClassName,
} from "./statisticsShadcnCardShell";

export type DistributionDonutCenter = {
  value: string;
  title: string;
};

export type DistributionPieDatum = {
  segment: string;
  label: string;
  trades: number;
  fill: string;
};

export interface DistributionPieLabelChartProps {
  title: string;
  description?: string;
  data: DistributionPieDatum[];
  config: ChartConfig;
  className?: string;
  center?: DistributionDonutCenter;
  tooltipFooter?: (segmentKey: string, label: string) => React.ReactNode | null;
}

export function DistributionPieLabelChart({
  title,
  description,
  data,
  config,
  className,
  center,
  tooltipFooter,
}: DistributionPieLabelChartProps) {
  const { resolvedTheme } = useTheme();
  const mutedTextFill = useStatsMutedTextFill();
  const total = useMemo(
    () => data.reduce((sum, row) => sum + (row.trades || 0), 0),
    [data]
  );

  const chartConfig = useMemo(
    () => ({
      trades: { label: "Trades" },
      ...config,
    }),
    [config]
  );

  const labelBySegment = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of data) {
      map.set(row.segment, row.label);
    }
    return map;
  }, [data]);

  const hasChart = data.length > 0 && total > 0 && center;

  /** Percentage radii keep the ring inside the plot; margin adds a small safety inset. */
  const pieChartMargin = { top: 4, right: 4, bottom: 4, left: 4 };

  const cardShellClassName = cn(statisticsShadcnCardClassName, className);

  return (
    <Card
      data-statistics-shadcn-card
      data-statistics-distribution-card=""
      className={cardShellClassName}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <CardHeader className={statisticsShadcnCardHeaderClassName}>
        <CardTitle
          className={statsCardHeadlineClassName}
          style={{ fontFamily: STATS_FONT_FAMILY }}
        >
          {title}
        </CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className={statisticsShadcnCardContentClassName}>
        {hasChart ? (
          <div className={statisticsShadcnCardChartAreaClassName}>
            <ChartContainer
              config={chartConfig}
              className={statisticsShadcnChartContainerClassName}
              data-statistics-distribution-donut=""
              style={{ fontFamily: STATS_FONT_FAMILY }}
            >
              <PieChart margin={pieChartMargin}>
              <ChartTooltip
                cursor={false}
                content={
                  tooltipFooter ? (
                    <ChartTooltipContent
                      className={statsChartTooltipContentClassName("w-[200px]")}
                      labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
                      nameKey="segment"
                      hideLabel
                      formatter={(value, _name, item) => {
                        const segment = String(item.payload?.segment ?? "");
                        const itemConfig = chartConfig[segment as keyof typeof chartConfig];
                        const configLabel =
                          itemConfig && "label" in itemConfig
                            ? itemConfig.label
                            : undefined;
                        const label =
                          labelBySegment.get(segment) ??
                          (typeof configLabel === "string" ? configLabel : segment);
                        const n = Number(value);
                        const pct =
                          total > 0 ? ((n / total) * 100).toFixed(1) : "0";
                        const footer = tooltipFooter(segment, label);

                        return (
                          <div className="grid w-full gap-1">
                            <StatisticsChartTooltipRow
                              label={label}
                              value={`${n} (${pct}%)`}
                            />
                            {footer}
                          </div>
                        );
                      }}
                    />
                  ) : (
                    <ChartTooltipContent
                      className={statsChartTooltipContentClassName("w-[200px]")}
                      labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
                      hideLabel
                      nameKey="segment"
                      formatter={(value, _name, item) => {
                        const segment = String(item.payload?.segment ?? "");
                        const itemConfig = chartConfig[segment as keyof typeof chartConfig];
                        const configLabel =
                          itemConfig && "label" in itemConfig
                            ? itemConfig.label
                            : undefined;
                        const label =
                          labelBySegment.get(segment) ??
                          (typeof configLabel === "string" ? configLabel : segment);
                        const n = Number(value);
                        const pct =
                          total > 0 ? ((n / total) * 100).toFixed(1) : "0";
                        return (
                          <StatisticsChartTooltipRow
                            label={label}
                            value={`${n} (${pct}%)`}
                          />
                        );
                      }}
                    />
                  )
                }
              />
              <Pie
                data={data}
                dataKey="trades"
                nameKey="segment"
                innerRadius="58%"
                outerRadius="82%"
                stroke={resolvedTheme === "dark" ? "var(--sidebar)" : "var(--card)"}
                strokeWidth={1}
              >
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{ fontFamily: STATS_FONT_FAMILY }}
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="text-3xl font-bold"
                            style={{
                              fontFamily: STATS_FONT_FAMILY,
                              fill: mutedTextFill,
                            }}
                          >
                            {center.value}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            style={{
                              fontFamily: STATS_FONT_FAMILY,
                              fill: mutedTextFill,
                            }}
                          >
                            {center.title}
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
              </PieChart>
            </ChartContainer>
          </div>
        ) : (
          <div className={statisticsShadcnCardChartAreaClassName}>
            <div
              className={cn(
                statisticsShadcnChartContainerClassName,
                "flex items-center justify-center text-center",
                STATISTICS_NO_DATA_TIMEFRAME_CLASSNAME
              )}
            >
              {STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function DistributionPieChartSkeleton({ className }: { className?: string }) {
  return (
    <Card
      data-statistics-shadcn-card
      className={cn(statisticsShadcnCardClassName, className)}
    >
      <CardHeader className={statisticsShadcnCardHeaderClassName}>
        <div className="h-4 w-[45%] animate-pulse rounded-md bg-muted" />
      </CardHeader>
      <CardContent className={statisticsShadcnCardContentClassName}>
        <div className={statisticsShadcnCardChartAreaClassName}>
          <div
            className={cn(
              statisticsShadcnChartContainerClassName,
              "animate-pulse rounded-md bg-muted"
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}
